import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import HeroSlider from '../../components/home/HeroSlider';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import logo from '../../assets/images/logo.jpeg';

// ─── IMPORT IMAGES FROM YOUR LIST ─────────────────────────────────────────────
// Gym images (using fitneseqp images)
import fitneseqp1 from '../../assets/images/Fitneseqp1.png';
import fitneseqp2 from '../../assets/images/fitneseqp2.png';
import fitneseqp3 from '../../assets/images/fitneseqp3.png';
import fitneseqp4 from '../../assets/images/fitneseqp4.png';
import fitneseqp5 from '../../assets/images/fitneseqp5.png';
import fitneseqp6 from '../../assets/images/fitneseqp6.png';
import fitneseqp7 from '../../assets/images/fitneseqp7.png';
import fitneseqp8 from '../../assets/images/fitneseqp8.png';
import fitneseqp9 from '../../assets/images/fitneseqp9.png';
import fitneseqp10 from '../../assets/images/fitneseqp10.png';
import fitneseqp11 from '../../assets/images/fitneseqp11.png';
import fitneseqp12 from '../../assets/images/fitneseqp12.png';

// Sauna images
import saunaPic from '../../assets/images/sauna pic.png';
import saunaPic2 from '../../assets/images/sauna pic 2.png';
import saunaPic3 from '../../assets/images/sauna pic 3.png';

// Steam images
import steamRoom from '../../assets/images/steam room.png';

// Locker images
import lockers1 from '../../assets/images/lockers1.png';
import lockers2 from '../../assets/images/lockers2.png';

// Bathroom image
import bathroom from '../../assets/images/bathroom.png';

// PCF Studio
import pcfStudioOut from '../../assets/images/pcf studio out.png';

// Entrance Gym Pic
import entranceGymPic from '../../assets/images/Entrance Gym pic.png';

// ─── NEW IMAGES ───────────────────────────────────────────────────────────────
import niceGymBanner from '../../assets/images/nice gym banner.jpg';
import wellPositioned from '../../assets/images/well positioned .jpg';

// ─── Service Card with Image Slider ───────────────────────────────────────────
const ServiceCard = ({ service, index, images }) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 3000 + index * 400);
    return () => clearInterval(timer);
  }, [index, images.length]);

  const prev = (e) => {
    e.stopPropagation();
    setCurrent((c) => (c - 1 + images.length) % images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setCurrent((c) => (c + 1) % images.length);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -10 }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden group cursor-pointer"
      onClick={() => (window.location.href = service.link)}
    >
      {/* Image Slider */}
      <div className="relative h-56 overflow-hidden">
        {images.map((img, i) => (
          <img
            key={i}
            src={img}
            alt={`${service.title} ${i + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 group-hover:scale-110 ${
              i === current ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionProperty: 'opacity, transform', transitionDuration: '700ms' }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10" />

        {/* Slider Controls */}
        <button
          onClick={prev}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 bg-white/30 hover:bg-white/60 rounded-full flex items-center justify-center backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100"
        >
          <ChevronLeftIcon className="h-4 w-4 text-white" />
        </button>
        <button
          onClick={next}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 bg-white/30 hover:bg-white/60 rounded-full flex items-center justify-center backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100"
        >
          <ChevronRightIcon className="h-4 w-4 text-white" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex space-x-1">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
              className={`w-1.5 h-1.5 rounded-full transition-all ${
                i === current ? 'bg-white w-3' : 'bg-white/50'
              }`}
            />
          ))}
        </div>

        {/* Service Icon */}
        <div className="absolute bottom-4 left-4 text-white z-20">
          <span className="text-2xl">{service.icon}</span>
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-base font-bold mb-1 group-hover:text-primary transition-colors">
          {service.title}
        </h3>
        <p className="text-gray-600 text-xs mb-3">{service.description}</p>
        <Link
          to={service.link}
          className="text-primary font-semibold inline-flex items-center space-x-1 group-hover:space-x-2 transition-all text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <span>Learn More</span>
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </Link>
      </div>
    </motion.div>
  );
};

// ─── Main Home Component ───────────────────────────────────────────────────────
const Home = () => {
  const PHONE = '+254117110000';

  // Image sets for each service - using your new images
  const serviceImageSets = {
    gym: [fitneseqp1, fitneseqp2, fitneseqp3, fitneseqp4],
    sauna: [saunaPic, saunaPic2, saunaPic3],
    steam: [steamRoom, steamRoom, steamRoom],
    yoga: [pcfStudioOut, fitneseqp5, fitneseqp6]
  };

  return (
    <>
      <Helmet>
        <title>PowerCore Fitness | Best Gym in Nairobi | Southfield Mall</title>
        <meta name="description" content="PowerCore Fitness is the premier gym in Nairobi located at Southfield Mall. Open 4AM-10PM weekdays & Saturday, 8AM-8PM Sunday. State-of-the-art equipment, personal training, yoga, sauna & steam room. Join today and transform your life!" />
        <meta name="keywords" content="gym Nairobi, fitness center Southfield Mall, PowerCore Fitness, best gym in Nairobi, gym membership Nairobi, personal training Nairobi, yoga classes Nairobi, sauna Nairobi, steam room Nairobi, fitness Kenya" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://pcfgym.co.ke/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content="PowerCore Fitness | Best Gym in Nairobi | Southfield Mall" />
        <meta property="og:description" content="Join PowerCore Fitness today! State-of-the-art gym equipment, expert trainers, yoga, sauna & steam room. Located at Southfield Mall, Nairobi." />
        <meta property="og:url" content="https://pcfgym.co.ke/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://pcfgym.co.ke/og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PowerCore Fitness | Best Gym in Nairobi" />
        <meta name="twitter:description" content="Transform your body at PowerCore Fitness. Premium gym facilities at Southfield Mall, Nairobi." />
        <meta name="twitter:image" content="https://pcfgym.co.ke/twitter-image.jpg" />
      </Helmet>

      <div className="min-h-screen">
        {/* 1. Hero Section with Slider */}
        <HeroSlider />

        {/* 2. Services Preview — 4 cards each with 3-image slider */}
        <section className="pb-12 bg-white" style={{ paddingTop: '15px' }}>
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <span className="text-primary font-semibold tracking-wider uppercase">Our Services</span>
              <h1 className="section-title" style={{ marginTop: '0', fontSize: 'clamp(1.8rem, 4vw, 2.5rem)' }}>
                <span className="text-primary">PREMIUM</span>{' '}
                <span className="text-secondary">FACILITIES</span>
              </h1>
              <p className="section-subtitle">
                Everything you need to achieve your fitness goals in one place
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <ServiceCard 
                  key={index} 
                  service={service} 
                  index={index} 
                  images={serviceImageSets[service.imageKey]}
                />
              ))}
            </div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="flex justify-center mt-10"
            >
              <Link
                to="/register"
                className="group relative inline-flex items-center gap-3 px-10 py-4 rounded-full font-bold text-sm tracking-widest uppercase text-white overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                style={{
                  background: 'linear-gradient(135deg, #800020 0%, #c0002f 100%)',
                  boxShadow: '0 8px 32px rgba(128,0,32,0.35)',
                  letterSpacing: '0.12em',
                }}
              >
                <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />
                <span>Get Started Today</span>
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* 3. Full-Width Banner (replaced with nice gym banner) */}
        <div className="w-full" style={{ height: '400px' }}>
          <img
            src={niceGymBanner}
            alt="PowerCore Fitness premium gym facility - modern equipment and spacious training area"
            className="w-full h-full object-cover"
          />
        </div>

        {/* 4. High-End Gym Section - Well Positioned Image on Left, Content on Right */}
        <section className="py-16 bg-white">
          <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Well Positioned Image */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className="relative rounded-2xl overflow-hidden shadow-2xl"
              >
                <img
                  src={wellPositioned}
                  alt="PowerCore Fitness well-positioned gym space with strategic equipment layout"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </motion.div>

              {/* Right Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <span className="text-primary font-semibold tracking-wider uppercase text-sm bg-primary/10 px-4 py-1.5 rounded-full inline-block">
                  State-of-the-Art Facility
                </span>
                <h2 className="text-3xl md:text-4xl font-black leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  A HIGH-END GYM WITH <span className="text-primary">MODERN EQUIPMENT</span>
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  PowerCore Fitness is designed for those who demand the best. Our facility features 
                  cutting-edge equipment from leading manufacturers, strategically positioned for 
                  optimal workflow and maximum training efficiency.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: '🏋️', text: 'Premium Strength Equipment' },
                    { icon: '🏃', text: 'Commercial Cardio Machines' },
                    { icon: '💪', text: 'Free Weights Section' },
                    { icon: '📊', text: 'Performance Tracking' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-lg">{item.icon}</span>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 pt-4">
                  <Link
                    to="/pricing"
                    className="group inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm text-white transition-all hover:scale-105"
                    style={{ background: '#800020', boxShadow: '0 4px 20px rgba(128,0,32,0.35)' }}
                  >
                    View Membership <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/facility"
                    className="group inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm text-gray-700 border-2 border-gray-300 hover:border-primary hover:text-primary transition-all"
                  >
                    Take a Tour <ArrowRightIcon className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 5. Why Choose Us — max 15px top/bottom padding */}
        <section className="bg-white" style={{ paddingTop: '15px', paddingBottom: '15px' }}>
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <span className="text-primary font-semibold tracking-wider uppercase bg-primary/10 px-4 py-1.5 rounded-full inline-block mb-2 text-sm">
                Why Choose Us
              </span>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                <span className="text-primary">EXPERIENCE</span>{' '}
                <span className="text-secondary">THE DIFFERENCE</span>
              </h2>
              <p className="text-gray-600 text-sm max-w-xl mx-auto">
                World-class facilities, expert guidance, and a community that supports your fitness journey
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group relative h-52 cursor-pointer"
                >
                  {/* DEFAULT STATE — gradient card (shown on load) */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-dark rounded-2xl shadow-xl p-5 flex flex-col items-center justify-center text-center transition-all duration-500 group-hover:opacity-0 group-hover:scale-95 overflow-hidden">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-3 backdrop-blur-sm">
                      <span className="text-2xl text-white">{feature.icon}</span>
                    </div>
                    <h3 className="text-sm font-bold text-white mb-1.5">{feature.title}</h3>
                    <p className="text-white/90 text-xs mb-3">{feature.description}</p>
                    <div className="flex items-center space-x-1 text-white text-xs font-semibold">
                      <span>Discover more</span>
                      <ArrowRightIcon className="h-3 w-3" />
                    </div>
                    <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -mr-4 -mt-4" />
                    <div className="absolute bottom-0 left-0 w-12 h-12 bg-white/10 rounded-full -ml-3 -mb-3" />
                  </div>

                  {/* HOVER STATE — white card */}
                  <div className="absolute inset-0 bg-white rounded-2xl shadow-lg p-5 flex flex-col items-center text-center opacity-0 group-hover:opacity-100 transition-all duration-500 scale-95 group-hover:scale-100">
                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-3">
                      <span className="text-2xl">{feature.icon}</span>
                    </div>
                    <h3 className="text-sm font-bold mb-1.5 text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600 text-xs leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. About Preview Section - UPDATED with correct about points */}
        <section className="py-12 bg-gray-50">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <span className="text-primary font-semibold tracking-wider uppercase">About Us</span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  <span className="text-primary">MORE THAN JUST</span> A GYM
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  PowerCore Fitness isn't just about lifting weights—it's about transforming lives.
                  Since 2020, we've helped thousands of members achieve their fitness goals through
                  innovative training methods, state-of-the-art equipment, and a supportive community.
                </p>
                <div className="space-y-4 mb-8">
                  {aboutPoints.map((point, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center space-x-3"
                    >
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      </div>
                      <span className="text-gray-700">{point}</span>
                    </motion.div>
                  ))}
                </div>
                <Link to="/about" className="btn-outline inline-flex items-center space-x-2 group">
                  <span>Learn More</span>
                  <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-4"
              >
                <img
                  src={fitneseqp7}
                  alt="PowerCore Fitness gym interior with modern equipment in Nairobi"
                  className="rounded-2xl shadow-xl h-64 object-cover"
                />
                <img
                  src={fitneseqp8}
                  alt="Personal training session at PowerCore Fitness gym Nairobi"
                  className="rounded-2xl shadow-xl h-64 object-cover mt-8"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* 7. CTA Section - Same style as Contact page */}
        <section className="py-14 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #800020 0%, #3d0010 100%)' }}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full filter blur-3xl" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
          </div>
          <div className="container-custom text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full inline-block mb-4 bg-white/20 text-white/80">
                Limited Spots Available
              </span>
              <h2 className="font-black text-white mb-4 leading-none"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
                READY TO START YOUR <span className="text-yellow-400">JOURNEY?</span>
              </h2>
              <p className="text-white/75 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
                Join PowerCore today and transform your life. No contracts. No excuses. Just results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/pricing"
                  className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-black text-sm text-gray-900 bg-white transition-all hover:scale-105 hover:shadow-2xl">
                  View Pricing & Join <ArrowRightIcon className="h-4 w-4" />
                </Link>
                <a href={`https://wa.me/${PHONE.replace(/\+/g, '')}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-bold text-sm text-white border-2 border-white/40 hover:bg-white/10 transition-all">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Chat on WhatsApp
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

// ─── Data ─────────────────────────────────────────────────────────────────────
const features = [
  {
    icon: '🏋️',
    title: 'Modern Equipment',
    description: 'State-of-the-art machines and free weights from top brands for optimal training results.'
  },
  {
    icon: '👥',
    title: 'Expert Trainers',
    description: 'Certified professionals dedicated to helping you achieve your fitness goals safely.'
  },
  {
    icon: '🌡️',
    title: 'Sauna & Steam',
    description: 'Premium recovery facilities included with every membership for post-workout relaxation.'
  },
  {
    icon: '🛍️',
    title: 'Pro Shop',
    description: 'Premium supplements, gear, and apparel at exclusive member prices.'
  }
];

// ─── UPDATED ABOUT POINTS as requested ────────────────────────────────────────
const aboutPoints = [
  'Classes every day',
  'Full gym access for premium members',
  'More than just a gym',
  'Personalized training',
  'Nutrition guidance'
];

// Updated services
const services = [
  { icon: '💪', title: 'Gym & Fitness', description: 'State-of-the-art equipment and free weights for optimal training.', link: '/services/gym', imageKey: 'gym' },
  { icon: '🌡️', title: 'Infrared Sauna', description: 'Detox and recover with premium infrared heat therapy.', link: '/services/sauna', imageKey: 'sauna' },
  { icon: '💨', title: 'Steam Room', description: 'Relax and rejuvenate in our luxurious steam room.', link: '/services/steam', imageKey: 'steam' },
  { icon: '🧘', title: 'Classes & Yoga', description: 'Diverse group classes and yoga sessions for all levels.', link: '/services/yoga', imageKey: 'yoga' }
];

export default Home;